import {Inject, Injectable} from '@angular/core'
import {Language} from './language'
import {BehaviorSubject, Observable} from 'rxjs'
import {DOCUMENT, Location} from '@angular/common'
import {TranslocoService} from '@ngneat/transloco'
import {Logger} from '../logging/logger.types'

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private static readonly defaultLanguageSupport = Object.values(Language)
  private static readonly _supportedLanguages = new BehaviorSubject<Language[]>([
    ...LanguageService.defaultLanguageSupport,
  ])

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly location: Location,
    private readonly transloco: TranslocoService,
    private readonly logger: Logger,
  ) {
    this.setActiveLanguage()
  }

  static allSupportedLanguages(): Language[] {
    return this._supportedLanguages.getValue()
  }

  private setActiveLanguage() {
    const supportedLanguages = this.supportedLanguages()
    const locale = this.getCurrentLocale(supportedLanguages)
    this.transloco.setActiveLang(locale)
    this.transloco.setAvailableLangs(supportedLanguages)
    this.setDocumentLanguage(locale)
    this.logger.debug(`Active lang set to ${locale}`)
  }

  supportedLanguages(): Language[] {
    return LanguageService._supportedLanguages.getValue()
  }

  get supportedLanguages$(): Observable<Language[]> {
    return LanguageService._supportedLanguages.asObservable()
  }

  private setDocumentLanguage(language: string) {
    if (this.document.documentElement.lang !== language) {
      this.document.documentElement.lang = language
    }
  }

  private getCurrentLocale(supportedLanguages: Language[]) {
    const defaultLang = Language.GERMAN
    return this.findLocaleInCurrentPath(supportedLanguages) || defaultLang
  }

  private findLocaleInCurrentPath(supportedLanguages: Language[]) {
    const locationUrl = this.location.path(false)

    const pathParts = locationUrl.split(/[/?#]/).filter(part => !!part)
    if (pathParts.length === 0) {
      return
    }
    const locale = pathParts[0]
    if (!supportedLanguages.includes(locale as Language)) {
      return
    }

    return locale as Language
  }
}
